// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Mutation hook for promoting an anomaly to an alarm — the only write an anomaly
// supports (see services/anomaliesApiService.ts's promoteAnomaly).

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { promoteAnomaly } from '../services/anomaliesApiService';
import { sendAlarmEvent } from '../services/alarmsService';
import { ApiError } from '../services/api';

export interface PromoteAnomalyVars {
  siteId: string;
  /** Addresses the row. The request is keyed on this, not on the rule code. */
  alarmId: string;
  /**
   * The rule code. NOT sent — it is only needed to invalidate the
   * ['anomaly', siteId, alarmCode] cache prefix, which queryKeys.anomaly()
   * builds in that order. Optional so a caller that only has the id can still
   * promote; the anomaly detail cache is simply left to its own staleTime.
   */
  alarmCode?: string;
}

/** Whether a failed promotion is worth offering a retry for. */
export function isPromoteRetryable(error: unknown): boolean {
  return error instanceof ApiError && error.status === 502;
}

/** Operator-facing copy for a failed promotion, keyed off the upstream status. */
export function promoteErrorMessage(error: unknown): string {
  const status = error instanceof ApiError ? error.status : undefined;
  switch (status) {
    case 404:
      return 'This anomaly no longer exists — it may already have moved to the alarms list.';
    case 409:
      return 'This anomaly is already an alarm — refresh the alarms list to find it.';
    case 502:
      return 'Promotion could not be published, so it was rolled back. Nothing changed — you can try again.';
    default:
      return error instanceof Error && error.message
        ? error.message
        : 'Could not promote this anomaly.';
  }
}

export function usePromoteAnomaly() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, PromoteAnomalyVars>({
    mutationFn: ({ siteId, alarmId }) => promoteAnomaly(siteId, alarmId),
    onSuccess: (_data, { siteId, alarmCode }) => {
      if (alarmCode) {
        queryClient.invalidateQueries({ queryKey: ['anomaly', siteId, alarmCode] });
      }
      queryClient.invalidateQueries({ queryKey: ['anomalies', siteId] });
      queryClient.invalidateQueries({ queryKey: ['alarms', siteId] });
      queryClient.invalidateQueries({ queryKey: ['console-alarms'] });
    },
  });
}

export interface CloseAnomalyVars {
  siteId: string;
  /** Addresses the row (apply-event is keyed on the occurrence id). */
  alarmId: string;
  /** Only used to invalidate the ['anomaly', siteId, alarmCode] cache prefix. */
  alarmCode?: string;
}

/** Operator-facing copy for a failed "Not an alarm" close. */
export function closeErrorMessage(error: unknown): string {
  const status = error instanceof ApiError ? error.status : undefined;
  switch (status) {
    case 404:
      return 'This anomaly no longer exists. It may already have moved to the alarms list.';
    case 409:
      return 'This anomaly changed state while you were looking at it. Refresh and try again.';
    default:
      return error instanceof Error && error.message
        ? error.message
        : 'Could not close this anomaly.';
  }
}

/**
 * "Not an alarm": closes a finding with the CLOSE operator event, the same
 * apply-event the alarms use (AEM keys findings and alarms on one alarm_id).
 */
export function useCloseAnomaly() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, CloseAnomalyVars>({
    mutationFn: ({ siteId, alarmId }) => sendAlarmEvent(siteId, alarmId, 'CLOSE'),
    onSuccess: (_data, { siteId, alarmCode }) => {
      if (alarmCode) {
        queryClient.invalidateQueries({ queryKey: ['anomaly', siteId, alarmCode] });
      }
      queryClient.invalidateQueries({ queryKey: ['anomalies', siteId] });
      queryClient.invalidateQueries({ queryKey: ['console-alarms'] });
    },
  });
}
